/**
 * 视频转视频压缩工具（ffmpeg.wasm 驱动）
 *----------------------------------------------------
 * 用途：把浏览器里的视频 File 用 H.264 + AAC 重新编码，得到体积更小的同类型视频。
 *
 * 使用：
 *   import { compressVideo, isFfmpegLoaded } from '@/utils/videoToVideo';
 *   const outFile = await compressVideo(inputFile, { ratio: 0.6, onProgress });
 *
 * 依赖静态资源（由 scripts/copy-media-assets.js 从 node_modules 复制到 public/vendor/ffmpeg/）：
 *   - /vendor/ffmpeg/ffmpeg-worker.js  转发 worker
 *   - /vendor/ffmpeg/ffmpeg-core.js    ffmpeg 内核 JS
 *   - /vendor/ffmpeg/ffmpeg-core.wasm  ffmpeg 内核 wasm（~30MB）
 *
 * 懒加载策略：
 *   - 首次调用 compressVideo 才动态 import '@ffmpeg/ffmpeg'（拆 chunk，首屏无成本）
 *   - 首次调用还会下载 ~30MB 的 wasm 内核，之后模块级缓存复用 FFmpeg 实例
 *
 * 参数语义（与 videoToGif 保持一致）：
 *   ratio ∈ (0, 1]，值越大越清晰、体积越大；值越小压得越狠、体积越小
 *   ratio → CRF 线性映射：ratio=1 → crf=18（视觉无损档），ratio→0 → crf=40（体积极小档）
 */

const FFMPEG_WORKER_URL = '/vendor/ffmpeg/ffmpeg-worker.js';
const FFMPEG_CORE_URL = '/vendor/ffmpeg/ffmpeg-core.js';
const FFMPEG_WASM_URL = '/vendor/ffmpeg/ffmpeg-core.wasm';

// 模块级单例：整个应用只加载一次 wasm 内核
let ffmpegInstance = null;
let ffmpegLoadingPromise = null;

// ffmpeg 首次加载超时（毫秒）
// 真机上 wasm 大约 30MB，弱网/受限网络下容易挂起；超时后视为失败，让上层降级到"原文件上传"。
// 20s 经验值：正常 4G 下 15-25s 完成；企微 X5 WebView 常态挂起，超过 20s 基本无解。
// 之前设 45s 用户等不了那么久（视觉上等同于"卡死"），改短更快降级。
const FFMPEG_LOAD_TIMEOUT_MS = 20 * 1000;

/**
 * 给一个 promise 套上超时保护，超时抛出可识别的 Error
 * @param {Promise<any>} promise
 * @param {number} ms
 * @param {string} label
 */
function withTimeout(promise, ms, label) {
  return new Promise((resolve, reject) => {
    let done = false;
    const timer = setTimeout(() => {
      if (done) return;
      done = true;
      const err = new Error(`${label} 超时（${Math.round(ms / 1000)}s）`);
      err.code = 'FFMPEG_TIMEOUT';
      reject(err);
    }, ms);
    promise.then(
      (v) => { if (!done) { done = true; clearTimeout(timer); resolve(v); } },
      (e) => { if (!done) { done = true; clearTimeout(timer); reject(e); } },
    );
  });
}

/**
 * 懒加载 ffmpeg.wasm 门面 + 内核
 * @param {(stage:string)=>void} [onStage]  可选 stage 回调（'load-ffmpeg-import' | 'load-ffmpeg-core'）
 * @returns {Promise<import('@ffmpeg/ffmpeg').FFmpeg>}
 */
async function getFfmpeg(onStage) {
  if (ffmpegInstance && ffmpegInstance.loaded) return ffmpegInstance;
  if (ffmpegLoadingPromise) return ffmpegLoadingPromise;

  // 说明：企微/微信 WebView 环境的分派现在由上层 mediaCompressor.js 处理
  // （优先走 MediaRecorder + Canvas 管线），此处不再直接 fast-fail。
  // 但保留 module worker 支持性预检，环境真的不满足时才抛不支持。

  // 提前校验 module worker 支持性：不支持直接抛错，避免真机上 60s+ 死等
  if (!canRunModuleWorker()) {
    const err = new Error('当前浏览器不支持 Module Worker，无法运行 ffmpeg.wasm');
    err.code = 'FFMPEG_UNSUPPORTED';
    throw err;
  }

  ffmpegLoadingPromise = (async () => {
    try { onStage && onStage('load-ffmpeg-import'); } catch (_) { /* ignore */ }
    // 动态 import：webpack 会把 ffmpeg 门面拆到独立 chunk，首屏零成本
    const mod = await import(/* webpackChunkName: "ffmpeg-wasm" */ '@ffmpeg/ffmpeg');
    const FFmpeg = mod.FFmpeg || (mod.default && mod.default.FFmpeg);
    if (!FFmpeg) throw new Error('@ffmpeg/ffmpeg 加载失败：未导出 FFmpeg 类');

    const ff = new FFmpeg();
    try { onStage && onStage('load-ffmpeg-core'); } catch (_) { /* ignore */ }
    // 给核心加载套超时：真机弱网可能永远不 resolve
    await withTimeout(
      ff.load({
        classWorkerURL: FFMPEG_WORKER_URL,
        coreURL: FFMPEG_CORE_URL,
        wasmURL: FFMPEG_WASM_URL,
      }),
      FFMPEG_LOAD_TIMEOUT_MS,
      'ffmpeg 内核加载',
    );
    ffmpegInstance = ff;
    return ff;
  })().catch((err) => {
    // 加载失败时清空 promise，允许下次重试
    ffmpegLoadingPromise = null;
    throw err;
  });

  return ffmpegLoadingPromise;
}

/**
 * ratio (0~1] → H.264 CRF (18~40)
 * CRF：Constant Rate Factor，H.264 常量码率因子；数值越大压缩越狠、画质越低
 *   18 视觉无损，23 是 ffmpeg 默认值，28+ 明显有损，40 极端压缩
 * 映射策略：ratio=1 → 18； ratio=0 → 40； 线性插值
 * @param {number} ratio
 * @returns {number} 整数 CRF
 */
function ratioToCrf(ratio) {
  const r = Math.max(0, Math.min(1, Number(ratio) || 0));
  return Math.round(40 - 22 * r);
}

/**
 * ratio (0~1] → 视频码率上限倍数（相对源估算）
 * 作为 CRF 的补充上限，防止画面复杂场景码率飙升
 * @param {number} ratio
 * @returns {string} ffmpeg -maxrate 参数（如 '1000k'）
 */
function ratioToMaxrateHint(ratio) {
  const r = Math.max(0, Math.min(1, Number(ratio) || 0));
  // 300k(极限压缩) ~ 4000k(高质量) 线性
  const kbps = Math.round(300 + 3700 * r);
  return `${kbps}k`;
}

/**
 * 保留原后缀，输出同格式文件名
 * @param {string} name
 * @returns {{ext: string, out: string}}
 */
function pickOutputName(name) {
  if (!name || typeof name !== 'string') return { ext: 'mp4', out: 'compressed.mp4' };
  const idx = name.lastIndexOf('.');
  if (idx <= 0) return { ext: 'mp4', out: `${name}.compressed.mp4` };
  const base = name.slice(0, idx);
  const ext = name.slice(idx + 1).toLowerCase();
  // ffmpeg 对某些容器名做别名映射：mov → mp4 更稳
  const normalizedExt = ext === 'mov' ? 'mp4' : ext;
  return { ext: normalizedExt, out: `${base}.compressed.${normalizedExt}` };
}

/**
 * 判断当前浏览器/环境是否具备运行 ffmpeg.wasm 的最基本前置条件
 * 注意：ffmpeg.wasm v0.12 已经不强依赖 SharedArrayBuffer（可跑单线程 core），
 *      因此仅检查 WebAssembly 支持即可
 * @returns {boolean}
 */
export function canRunFfmpeg() {
  return typeof WebAssembly !== 'undefined' && typeof Worker !== 'undefined';
}

/**
 * 探测当前环境是否支持 ES Module Worker（ffmpeg.wasm v0.12 强依赖）
 * 部分移动端 WebView（微信 X5、iOS Safari < 15、老 Android WebView）不支持，
 * 此时创建 module worker 会失败或静默挂起。提前检测可以快速降级，避免 60s+ 死等。
 *
 * 检测方式：尝试创建一个空 blob 的 module worker，看 Worker 构造是否抛错。
 * 这是同步创建、异步销毁，性能开销可以忽略。
 * @returns {boolean}
 */
export function canRunModuleWorker() {
  if (typeof Worker === 'undefined' || typeof Blob === 'undefined' || typeof URL === 'undefined') {
    return false;
  }
  let url = null;
  let w = null;
  try {
    const blob = new Blob(['self.close();'], { type: 'text/javascript' });
    url = URL.createObjectURL(blob);
    w = new Worker(url, { type: 'module' });
    return true;
  } catch (_) {
    return false;
  } finally {
    try { w && w.terminate(); } catch (_) { /* ignore */ }
    try { url && URL.revokeObjectURL(url); } catch (_) { /* ignore */ }
  }
}

/**
 * 识别是否为微信/企业微信 WebView 环境
 * 企微 WebView（尤其 Android X5 内核）对 ffmpeg.wasm 的兼容性极差：
 *   - Module Worker 的 fetch 可能挂起（检测能通过但真实加载不会 resolve）
 *   - 30MB wasm 请求偶发被 X5 网络层拦截
 *   - COOP/COEP 头缺失导致 SharedArrayBuffer 相关检测被卡
 * 因此企微环境直接跳过 ffmpeg 压缩，走"原文件上传"最稳。
 * @returns {boolean}
 */
export function isWechatWebView() {
  if (typeof navigator === 'undefined') return false;
  const ua = (navigator.userAgent || '').toLowerCase();
  return ua.includes('micromessenger') || ua.includes('wxwork');
}

/**
 * 主函数：把视频文件压缩到更小体积（保持同格式，或 mov→mp4）
 * @param {File|Blob} file
 * @param {Object} [options]
 * @param {number} [options.ratio=0.6]         压缩比例（越大越清晰）
 * @param {(p:number)=>void} [options.onProgress]  进度回调 0~1
 * @param {(stage:string)=>void} [options.onStage] 阶段回调（'load-ffmpeg-import' | 'load-ffmpeg-core' | 'transcoding'）
 * @param {AbortSignal} [options.signal]       取消信号（可选）
 * @returns {Promise<File>}
 */
export async function compressVideo(file, options = {}) {
  if (!file) throw new Error('文件为空');
  if (!canRunFfmpeg()) throw new Error('当前环境不支持 WebAssembly / Worker');

  const { ratio = 0.6, onProgress, onStage } = options;
  const crf = ratioToCrf(ratio);
  const maxrate = ratioToMaxrateHint(ratio);
  const { ext, out } = pickOutputName(file.name || 'input.mp4');

  const ff = await getFfmpeg(onStage);
  try { onStage && onStage('transcoding'); } catch (_) { /* ignore */ }

  // 进度回调：ffmpeg 会通过 'progress' 事件回传 { progress: 0~1, time }
  const progressHandler = ({ progress }) => {
    if (typeof onProgress === 'function') {
      try {
        onProgress(Math.max(0, Math.min(1, progress)));
      } catch (_) { /* ignore */ }
    }
  };
  ff.on('progress', progressHandler);

  const inputName = `input.${(file.name && file.name.split('.').pop()) || 'mp4'}`;
  const outputName = `output.${ext}`;

  try {
    // 1) 写入内存 FS
    const util = await import(/* webpackChunkName: "ffmpeg-wasm" */ '@ffmpeg/util');
    const inputBytes = await util.fetchFile(file);
    await ff.writeFile(inputName, inputBytes);

    // 2) 执行转码
    // -c:v libx264   使用 H.264（最广兼容）
    // -crf N         恒定质量因子
    // -preset ultrafast  浏览器端 wasm 编码，优先速度
    // -maxrate/-bufsize  码率上限，避免复杂场景膨胀
    // -c:a aac -b:a 96k   音频统一 AAC 96kbps
    // -movflags +faststart  moov 前置，方便流式播放
    const args = [
      '-i', inputName,
      '-c:v', 'libx264',
      '-preset', 'ultrafast',
      '-crf', String(crf),
      '-maxrate', maxrate,
      '-bufsize', maxrate,
      '-c:a', 'aac',
      '-b:a', '96k',
      '-movflags', '+faststart',
      '-y',
      outputName,
    ];
    await ff.exec(args);

    // 3) 读出并封装为 File
    const outBytes = await ff.readFile(outputName);
    const blob = new Blob([outBytes.buffer], { type: `video/${ext === 'mp4' ? 'mp4' : ext}` });
    let outFile;
    try {
      outFile = new File([blob], out, { type: blob.type });
    } catch (_) {
      outFile = blob;
      outFile.name = out;
    }

    // 4) 清理内存 FS
    try { await ff.deleteFile(inputName); } catch (_) { /* ignore */ }
    try { await ff.deleteFile(outputName); } catch (_) { /* ignore */ }

    return outFile;
  } finally {
    try { ff.off && ff.off('progress', progressHandler); } catch (_) { /* ignore */ }
  }
}

/**
 * 判断 ffmpeg 是否已加载完成（供上层做"首次转换 loading" 提示）
 * @returns {boolean}
 */
export function isFfmpegLoaded() {
  return !!(ffmpegInstance && ffmpegInstance.loaded);
}

export default compressVideo;
